import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl, money, safeHtml } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Notice, PageShell, ProductVisual, usePublishedPolicies } from "@/components/site/Layout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Pending = ({ label }) => <p className="text-sm text-[#7a4d18] bg-[#A66B24]/10 rounded px-3 py-2" data-testid={`pending-${label}`}>{label} information is pending owner confirmation and will be published before launch.</p>;

export default function Shop() {
  const { config, setCart, user } = useStore();
  const policies = usePublishedPolicies();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [idx, setIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/products/honey-nuts-750g").then((r) => setP(r.data)).catch((e) => setErr(errMsg(e)));
    api.get("/content/faq").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);
  if (err) return <PageShell><Notice tone="error">{err}</Notice></PageShell>;
  if (!p) return <PageShell><p aria-busy="true">Loading…</p></PageShell>;
  const imgs = p.images || [];
  const cur = imgs[idx];
  const canBuy = p.purchasable || (config?.mode === "ADMIN_TEST" && ["owner", "manager"].includes(user?.role));
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cart/items", { product_id: p.id, qty });
      setCart(data);
      toast.success("Added to cart");
      nav("/cart");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <PageShell>
      <div className="grid md:grid-cols-2 gap-12">
        <div>
          <ProductVisual image={cur ? { src: imgUrl(cur.url), alt: cur.alt, is_concept: cur.is_concept } : null} className="aspect-square w-full" />
          {imgs.length > 1 && (
            <div className="flex gap-3 mt-4" role="list">
              {imgs.map((im, i) => (
                <button key={im.id} onClick={() => setIdx(i)} aria-label={`Show image ${i + 1}`} className={`w-20 h-20 rounded overflow-hidden border-2 ${i === idx ? "border-[#A66B24]" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                  <img src={imgUrl(im.url)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="eyebrow mb-3">{p.subtitle}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl" data-testid="product-name">{p.name}</h1>
          <p className="mt-2 text-sm text-[#3d5248]">Net weight {p.net_weight} · {p.container}</p>
          <p className="serif text-4xl mt-6" data-testid="product-price">{money(p.price_cents)}</p>
          <p className="text-xs text-[#5b6b62]">Plus delivery. Prices in AUD.</p>
          <p className="mt-4 text-sm font-medium" data-testid="product-availability">
            {p.availability === "in_stock" ? "In stock" : p.availability === "out_of_stock" ? "Currently out of stock" : "Not yet available — join the launch list to hear when it is."}
          </p>
          <p className="mt-6 leading-relaxed">{p.description}</p>
          {canBuy ? (
            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center border border-[#d9d0b6] rounded-md" role="group" aria-label="Quantity">
                <button className="p-3" onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease quantity" data-testid="qty-decrease"><Minus size={16} /></button>
                <span className="w-10 text-center tabular-nums" data-testid="qty-value" aria-live="polite">{qty}</span>
                <button className="p-3" onClick={() => setQty(Math.min(20, qty + 1))} aria-label="Increase quantity" data-testid="qty-increase"><Plus size={16} /></button>
              </div>
              <button className="hn-btn" onClick={add} disabled={busy} data-testid="add-to-cart-btn">{busy ? "Adding…" : "Add to cart"}</button>
            </div>
          ) : config?.mode !== "LIVE" ? (
            <Link to="/#launch-list" className="hn-btn mt-8" data-testid="product-join-launch-btn">Join the launch list</Link>
          ) : null}
          {config?.mode === "ADMIN_TEST" && <p className="mt-3 text-xs text-[#7a4d18]">Test mode: only authorised staff can check out, using Stripe TEST payments.</p>}

          <section className="mt-12" aria-labelledby="ing-h">
            <h2 id="ing-h" className="text-2xl mb-3">Ingredients</h2>
            <p className="text-sm leading-relaxed" data-testid="product-ingredients">
              {p.ingredients.map((i, n) => <span key={i.name}>{i.allergen ? <strong>{i.name}</strong> : i.name} ({i.pct}%){n < p.ingredients.length - 1 ? ", " : "."}</span>)}
            </p>
            <p className="mt-4 font-semibold border-l-4 border-[#A66B24] pl-3 py-1" data-testid="product-allergen-statement">{p.allergen_statement}</p>
            {!p.ingredients_confirmed && <p className="text-xs text-[#5b6b62] mt-2">Final label wording is pending owner confirmation.</p>}
          </section>
          <section className="mt-10 grid gap-4">
            <h2 className="text-2xl">Nutrition and storage</h2>
            {p.nutrition ? <div className="text-sm whitespace-pre-line" data-testid="product-nutrition">{p.nutrition}</div> : <Pending label="Nutrition" />}
            {p.storage ? <p className="text-sm">{p.storage}</p> : <Pending label="Storage" />}
            {p.shelf_life ? <p className="text-sm">{p.shelf_life}</p> : <Pending label="Shelf life" />}
            {p.origin ? <p className="text-sm">{p.origin}</p> : <Pending label="Origin" />}
          </section>
          <section className="mt-10">
            <h2 className="text-2xl mb-2">Delivery</h2>
            <p className="text-sm">Delivered within Australia to supported postcodes. Delivery is calculated at checkout. No pickup.{policies.includes("shipping") && <> See our <Link to="/policies/shipping" className="underline">shipping policy</Link>.</>}</p>
          </section>
          <section className="mt-10">
            <h2 className="text-2xl mb-2">Serving suggestions</h2>
            <ul className="list-disc pl-5 text-sm space-y-1">{p.serving_suggestions.map((s) => <li key={s}>{s}</li>)}</ul>
          </section>
          {faqs.length > 0 && (
            <section className="mt-10">
              <h2 className="text-2xl mb-2">Questions</h2>
              <Accordion type="single" collapsible>
                {faqs.map((f) => (
                  <AccordionItem key={f.id} value={f.id}>
                    <AccordionTrigger className="text-left">{f.title}</AccordionTrigger>
                    <AccordionContent><div className="prose-hn text-sm" dangerouslySetInnerHTML={safeHtml(f.body_html)} /></AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}
        </div>
      </div>
    </PageShell>
  );
}
